import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Loader2, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function Copy({ service, terms, store, copyLabel }) {
  return (
    <div className="print-copy bg-white text-black px-3 py-2 text-[8.5px] leading-tight">
      {/* Header */}
      <div className="flex justify-between items-start border-b border-black pb-1.5 mb-1.5">
        <div className="min-w-0">
          <div className="text-sm font-display font-bold tracking-tight leading-none">{store.name}</div>
          {store.address && <div className="text-[7.5px] text-[#475569] whitespace-pre-line leading-tight">{store.address}</div>}
          <div className="text-[7.5px] text-[#475569] leading-tight">
            {store.phone && <span>Tel: {store.phone}</span>}
            {store.phone && store.email && <span> · </span>}
            {store.email && <span>{store.email}</span>}
          </div>
        </div>
        <div className="text-right shrink-0 pl-2">
          <div className="text-[7px] font-mono uppercase tracking-wider text-[#475569]">{copyLabel}</div>
          <div className="text-[11px] font-display font-bold leading-tight">Service Receipt</div>
          <div className="font-mono text-[10px] leading-tight">{service.service_id}</div>
          <div className="font-mono text-[7.5px] text-[#475569] leading-tight">{(service.created_at || "").slice(0, 10)}</div>
        </div>
      </div>

      {/* Customer + Device */}
      <div className="grid grid-cols-2 gap-3 mb-1.5">
        <div>
          <div className="text-[7px] font-mono uppercase tracking-wider text-[#475569]">Customer</div>
          <div className="font-semibold leading-tight">{service.customer_name}</div>
          <div className="font-mono text-[8px] leading-tight">{service.mobile}</div>
          {service.email && <div className="text-[7.5px] leading-tight truncate">{service.email}</div>}
        </div>
        <div>
          <div className="text-[7px] font-mono uppercase tracking-wider text-[#475569]">Device</div>
          <div className="leading-tight font-medium">{service.brand} {service.model_name}</div>
          <div className="text-[7.5px] text-[#475569] leading-tight">{service.device_type}</div>
          {service.serial_number && <div className="font-mono text-[7.5px] leading-tight">S/N: {service.serial_number}</div>}
        </div>
      </div>

      {/* Items + Problem */}
      <div className="mb-1.5">
        <span className="text-[7px] font-mono uppercase tracking-wider text-[#475569]">Items Received: </span>
        <span className="text-[8px]">
          {(service.items_received || []).length === 0 ? "—" : service.items_received.join(", ")}
        </span>
      </div>

      <div className="mb-1.5">
        <div className="text-[7px] font-mono uppercase tracking-wider text-[#475569]">Problem Description</div>
        <div className="text-[8px] border border-[#CBD5E1] rounded px-1.5 py-1 whitespace-pre-line min-h-[18px] leading-tight">
          {service.problem_description}
        </div>
      </div>

      {/* Terms */}
      <div className="mb-2">
        <div className="text-[7px] font-mono uppercase tracking-wider text-[#475569]">Terms & Conditions</div>
        <div className="text-[7px] text-[#334155] whitespace-pre-line leading-snug">
          {terms}
        </div>
      </div>

      {/* Signature */}
      <div className="pt-3 max-w-[50%]">
        <div className="border-t border-black pt-0.5 text-[8px] font-medium">Customer Signature</div>
        <div className="text-[6.5px] text-[#64748B] font-mono uppercase tracking-wider">I agree to the terms</div>
      </div>
    </div>
  );
}

export default function ServiceReceipt() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [terms, setTerms] = useState("");
  const [store, setStore] = useState({ name: "Digital Yantra", address: "", phone: "", email: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, t, st] = await Promise.all([
          api.get(`/services/${id}`),
          api.get(`/settings/terms`),
          api.get(`/settings/store`),
        ]);
        setService(s.data);
        setTerms(t.data.content || "");
        setStore(st.data || store);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0055FF]" /></div>;
  }
  if (!service) return <div className="p-8">Service not found.</div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-6">
      <div className="no-print max-w-3xl mx-auto px-6 mb-4 flex items-center justify-between">
        <Link to="/services">
          <Button variant="outline" className="gap-2" data-testid="back-to-services"><ArrowLeft className="w-4 h-4" /> Back</Button>
        </Link>
        <Button onClick={() => window.print()} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="print-receipt-btn">
          <Printer className="w-4 h-4" /> Print A5 (single sheet)
        </Button>
      </div>

      {/* On-screen preview shows the A5 sheet at scale */}
      <div className="no-print max-w-3xl mx-auto px-6 mb-2">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">A5 Preview · tear along dashed line for customer copy</div>
      </div>

      <div className="print-area mx-auto max-w-[148mm] bg-white shadow-sm border border-[#E2E8F0] print:shadow-none print:border-0">
        <Copy service={service} terms={terms} store={store} copyLabel="Customer Copy" />
        <div className="tear-line flex items-center gap-2 px-3 py-1 border-y border-dashed border-[#94A3B8] text-[#64748B] text-[8px] font-mono uppercase tracking-widest justify-center">
          <Scissors className="w-3 h-3" /> Tear here · Store copy below
        </div>
        <Copy service={service} terms={terms} store={store} copyLabel="Store Copy" />
      </div>
    </div>
  );
}
